import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Switch,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import AddIcon from "@mui/icons-material/Add";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { useState } from "react";
import SwitchToggle from "../../components/SwitchToggle";
import React from "react";

const ManageProductListing = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = React.useState("default");

  type Product = {
    id: number;
    title: string;
    description: string;
    code: string;
    status: "Pending" | "Approved" | "Rejected" | "Reported";
    postedBy: {
      id: number;
      name: string;
      email: string;
      phone: string;
    };
    price: number;
    currency: string;
    category: string;
    subcategory: string;
    images: string[];
    date: string;
    rejectionReason?: string;
    reports?: {
      reason: string;
      reportedBy: string;
      date: string;
      evidence?: string;
    }[];
    views?: number;
  };

  const [stores, setProducts] = useState<Product[]>([
    {
      id: 1,
      title: "Vintage Camera",
      description: "Classic vintage camera in excellent condition",
      code: "PROD001",
      status: "Pending",
      postedBy: {
        id: 101,
        name: "John Doe",
        email: "john@example.com",
        phone: "+1234567890",
      },
      price: 150,
      currency: "USD",
      category: "Electronics",
      subcategory: "Cameras",
      images: ["/static/images/placeholder.png"],
      date: "01-Sept-2025",
      views: 0,
    },
    {
      id: 2,
      title: "Designer Handbag",
      code: "PROD002",
      description: "Authentic designer handbag from 2023 collection",
      status: "Approved",
      postedBy: {
        id: 102,
        name: "Jane Smith",
        email: "jane@example.com",
        phone: "+1234567891",
      },
      price: 299,
      currency: "USD",
      category: "Fashion",
      subcategory: "Bags",
      images: ["/static/images/placeholder.png"],
      date: "01-Sept-2025",
      views: 45,
    },
    {
      id: 3,
      title: "Programming Books Bundle",
      code: "PROD003",
      description: "Collection of programming books for beginners",
      status: "Reported",
      postedBy: {
        id: 103,
        name: "Mike Johnson",
        email: "mike@example.com",
        phone: "+1234567892",
      },
      price: 50,
      currency: "USD",
      category: "Books",
      subcategory: "Educational",
      images: ["/static/images/placeholder.png"],
      date: "01-Sept-2025",
      views: 23,
      reports: [
        {
          reason: "Misleading description",
          reportedBy: "user456",
          date: "02-Sept-2025",
        },
      ],
    },
    {
      id: 4,
      title: "Fake Product",
      code: "PROD004",
      description: "Counterfeit item",
      status: "Rejected",
      postedBy: {
        id: 104,
        name: "Sarah Wilson",
        email: "sarah@example.com",
        phone: "+1234567893",
      },
      price: 25,
      currency: "USD",
      category: "Others",
      subcategory: "Miscellaneous",
      images: ["/static/images/placeholder.png"],
      date: "01-Sept-2025",
      rejectionReason: "Suspicious / fraudulent post",
      views: 5,
    },
  ]);

  const [tab, setTab] = useState(0);
  const handleTab = (_: React.SyntheticEvent, v: number) => setTab(v);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectProductId, setRejectProductId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectCustom, setRejectCustom] = useState("");

  const [warnOpen, setWarnOpen] = useState(false);
  const [warnProductId, setWarnProductId] = useState<number | null>(null);
  const [warnMessage, setWarnMessage] = useState("");

  const [reportDetailsOpen, setReportDetailsOpen] = useState(false);
  const [selectedReportedProduct, setSelectedReportedProduct] =
    useState<Product | null>(null);

  const rejectionPresets = [
    "Inappropriate content",
    "Misleading title/description",
    "Policy violation",
    "Duplicate listing",
    "Suspicious / fraudulent post",
  ];

  const filteredProducts = (status: Product["status"]) =>
    stores.filter((p) => p.status === status);

  const approveProduct = (id: number) => {
    setProducts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, status: "Approved" } : p))
    );
    // In real app: Send approval notification, log action, etc.
    console.log(`Product ${id} approved`);
  };

  const openReject = (id: number) => {
    setRejectProductId(id);
    setRejectReason("");
    setRejectCustom("");
    setRejectOpen(true);
  };

  const confirmReject = () => {
    if (rejectProductId == null) return;
    const reason = rejectCustom.trim() || rejectReason;
    setProducts((prev) =>
      prev.map((p) =>
        p.id === rejectProductId
          ? { ...p, status: "Rejected", rejectionReason: reason }
          : p
      )
    );
    // In real app: Send rejection notification, log action, etc.
    console.log(`Product ${rejectProductId} rejected: ${reason}`);
    setRejectOpen(false);
    setRejectProductId(null);
  };

  const openWarn = (id: number) => {
    setWarnProductId(id);
    setWarnMessage("");
    setWarnOpen(true);
  };

  const sendWarn = () => {
    if (warnProductId == null) return;
    // In real app: Send warning to user, log action, etc.
    console.log("Warning sent", { id: warnProductId, message: warnMessage });
    setWarnOpen(false);
    setWarnProductId(null);
  };

  const toggleProductStatus = (id: number, currentStatus: boolean) => {
    setProducts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, isActive: !currentStatus } : p))
    );
  };

  const openReportDetails = (product: Product) => {
    setSelectedReportedProduct(product);
    setReportDetailsOpen(true);
  };

  const dismissReport = (productId: number) => {
    setProducts((prev) =>
      prev.map((p) =>
        p.id === productId ? { ...p, status: "Approved", reports: [] } : p
      )
    );
    setReportDetailsOpen(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Approved":
        return "success";
      case "Pending":
        return "warning";
      case "Rejected":
        return "error";
      case "Reported":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <>
      <div className="main_title">
        <h1>Manage Product Listing</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          Manage Product Listing
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="Search stores..."
            />
          </div>
        </div>

        <Box className="custom_tabs1" sx={{ width: "100%", mt: 2 }}>
          <Tabs value={tab} onChange={handleTab} className="custom_tabs_links">
            <Tab label={`Pending (${filteredProducts("Pending").length})`} />
            <Tab label={`Approved (${filteredProducts("Approved").length})`} />
            <Tab label={`Rejected (${filteredProducts("Rejected").length})`} />
            <Tab label={`Reported (${filteredProducts("Reported").length})`} />
          </Tabs>
        </Box>

        {/* Pending Tab */}
        {tab === 0 && (
          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow hover>
                  <TableCell>Product ID</TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell>Title & Description</TableCell>
                  <TableCell>Posted By</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts("Pending").map((product) => (
                  <TableRow hover key={product.id}>
                    <TableCell>{product.code}</TableCell>
                    <TableCell>
                      <Box>
                        <img
                          style={{ height: 40, width: 80, objectFit: "cover" }}
                          src={
                            product.images[0] ||
                            "/static/images/placeholder.png"
                          }
                          alt={product.title}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <div>
                        <strong>{product.title}</strong>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "0.8rem",
                            color: "#666",
                          }}
                        >
                          {product.description}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Box
                        component="a"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(
                            `/manage-users/detail/${product.postedBy.id}`
                          );
                        }}
                        className="user_block click_action"
                      >
                        <p>
                          <b>{product.postedBy.name}</b>
                          <span>{product.postedBy.email}</span>
                          <span>{product.postedBy.phone}</span>
                        </p>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {product.price} {product.currency}
                    </TableCell>
                    <TableCell>
                      {product.category}
                      <br />
                      <small>{product.subcategory}</small>
                    </TableCell>
                    <TableCell>{product.date}</TableCell>
                    <TableCell>
                      <Chip
                        label={product.status}
                        color={getStatusColor(product.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="btn_flex">
                        <Button
                          className="btn btn_secondary"
                          onClick={() => approveProduct(product.id)}
                          size="small"
                        >
                          Approve
                        </Button>
                        <Button
                          className="btn btn_danger"
                          onClick={() => openReject(product.id)}
                          size="small"
                        >
                          Reject
                        </Button>
                        <div className="table_actions v2">
                          <IconButton
                            onClick={() => navigate(`/manage-products/detail`)}
                            size="small"
                          >
                            <img src="/static/images/eye_icon.svg" alt="View" />
                          </IconButton>
                          <IconButton
                            onClick={() => navigate(`/manage-products/add`)}
                            size="small"
                          >
                            <img
                              src="/static/images/edit2_icon.svg"
                              alt="Edit"
                            />
                          </IconButton>
                          <IconButton onClick={() => openReject(product.id)}>
                            <img
                              src="/static/images/trash2_icon.svg"
                              alt="Deactivate"
                            />
                          </IconButton>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Approved Tab */}
        {tab === 1 && (
          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow hover>
                  <TableCell>Product ID</TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Posted By</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Views</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts("Approved").map((product) => (
                  <TableRow hover key={product.id}>
                    <TableCell>{product.code}</TableCell>
                    <TableCell>
                      <Box>
                        <img
                          style={{ height: 40, width: 80, objectFit: "cover" }}
                          src={
                            product.images[0] ||
                            "/static/images/placeholder.png"
                          }
                          alt={product.title}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>{product.title}</TableCell>
                    <TableCell>
                      <Box className="user_block">
                        <p>
                          <b>{product.postedBy.name}</b>
                          <span>{product.postedBy.email}</span>
                        </p>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {product.price} {product.currency}
                    </TableCell>
                    <TableCell>
                      {product.category}
                      <br />
                      <small>{product.subcategory}</small>
                    </TableCell>
                    <TableCell>{product.date}</TableCell>
                    <TableCell>{product.views || 0}</TableCell>
                    <TableCell>
                      <Switch />
                      {/* <Chip 
                        label={product.status} 
                        color={getStatusColor(product.status) as any}
                        size="small"
                      /> */}
                    </TableCell>
                    <TableCell>
                      <div className="table_actions v2">
                        <IconButton
                          onClick={() => navigate(`/manage-products/detail`)}
                        >
                          <img src="/static/images/eye_icon.svg" alt="View" />
                        </IconButton>
                        <IconButton
                          onClick={() => navigate(`/manage-products/add`)}
                        >
                          <img src="/static/images/edit2_icon.svg" alt="Edit" />
                        </IconButton>
                        <IconButton onClick={() => openReject(product.id)}>
                          <img
                            src="/static/images/trash2_icon.svg"
                            alt="Deactivate"
                          />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Rejected Tab */}
        {tab === 2 && (
          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow hover>
                  <TableCell>Product ID</TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Posted By</TableCell>
                  <TableCell>Rejection Reason</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts("Rejected").map((product) => (
                  <TableRow hover key={product.id}>
                    <TableCell>{product.code}</TableCell>
                    <TableCell>
                      <Box>
                        <img
                          style={{ height: 40, width: 80, objectFit: "cover" }}
                          src={
                            product.images[0] ||
                            "/static/images/placeholder.png"
                          }
                          alt={product.title}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>{product.title}</TableCell>
                    <TableCell>
                      <Box className="user_block">
                        <p>
                          <b>{product.postedBy.name}</b>
                          <span>{product.postedBy.email}</span>
                        </p>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {product.rejectionReason || "No reason provided"}
                    </TableCell>
                    <TableCell>{product.date}</TableCell>
                    <TableCell>
                      <div className="table_actions v2">
                        <IconButton
                          onClick={() => navigate(`/manage-products/detail`)}
                        >
                          <img src="/static/images/eye_icon.svg" alt="View" />
                        </IconButton>
                        <IconButton
                          onClick={() => navigate(`/manage-products/add`)}
                        >
                          <img src="/static/images/edit2_icon.svg" alt="Edit" />
                        </IconButton>
                        <IconButton onClick={() => openReject(product.id)}>
                          <img
                            src="/static/images/trash2_icon.svg"
                            alt="Deactivate"
                          />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Reported Tab */}
        {tab === 3 && (
          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow hover>
                  <TableCell>Product ID</TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Posted By</TableCell>
                  <TableCell>Reports Count</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Report Actions</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts("Reported").map((product) => (
                  <TableRow hover key={product.id}>
                    <TableCell>{product.code}</TableCell>
                    <TableCell>
                      <Box>
                        <img
                          style={{ height: 40, width: 80, objectFit: "cover" }}
                          src={
                            product.images[0] ||
                            "/static/images/placeholder.png"
                          }
                          alt={product.title}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>{product.title}</TableCell>
                    <TableCell>
                      <Box className="user_block">
                        <p>
                          <b>{product.postedBy.name}</b>
                          <span>{product.postedBy.email}</span>
                        </p>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Button
                        onClick={() => openReportDetails(product)}
                        size="small"
                      >
                        {product.reports?.length || 0} Reports
                      </Button>
                    </TableCell>
                    <TableCell>{product.date}</TableCell>
                    <TableCell>
                      <div className="btn_flex">
                        <Button
                          className="btn btn_danger"
                          onClick={() => openReject(product.id)}
                          size="small"
                        >
                          Remove
                        </Button>
                        <Button
                          className="btn btn_grey"
                          onClick={() => openWarn(product.id)}
                          size="small"
                        >
                          Warn Seller
                        </Button>
                        <Button
                          className="btn btn_grey"
                          onClick={() => dismissReport(product.id)}
                          size="small"
                        >
                          Dismiss Report
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="table_actions v2">
                        <IconButton
                          onClick={() => navigate(`/manage-products/detail`)}
                        >
                          <img src="/static/images/eye_icon.svg" alt="View" />
                        </IconButton>
                        <IconButton
                          onClick={() => navigate(`/manage-products/add`)}
                        >
                          <img src="/static/images/edit2_icon.svg" alt="Edit" />
                        </IconButton>
                        <IconButton onClick={() => openReject(product.id)}>
                          <img
                            src="/static/images/trash2_icon.svg"
                            alt="Deactivate"
                          />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Reject Dialog */}
        <Dialog
          open={rejectOpen}
          onClose={() => setRejectOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Reject Product Listing</DialogTitle>
          <DialogContent className="form">
            <div className="gap_p">
              <div className="control_group w_100">
                <label>Choose a reason</label>
                <Select
                  fullWidth
                  hiddenLabel
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value as string)}
                >
                  <MenuItem value="">Select Reason</MenuItem>
                  {rejectionPresets.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <div className="control_group w_100">
                <label>Or enter custom reason</label>
                <TextField
                  fullWidth
                  hiddenLabel
                  value={rejectCustom}
                  onChange={(e) => setRejectCustom(e.target.value)}
                  placeholder="Enter custom rejection reason..."
                />
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectOpen(false)} className="btn">
              Cancel
            </Button>
            <Button
              className="btn btn_primary"
              onClick={confirmReject}
              // disabled={!rejectCustom.trim() && !rejectReason}
            >
              Reject Listing
            </Button>
          </DialogActions>
        </Dialog>

        {/* Warn Dialog */}
        <Dialog
          open={warnOpen}
          onClose={() => setWarnOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Send Warning to Seller</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Type warning message to the seller..."
              value={warnMessage}
              onChange={(e) => setWarnMessage(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setWarnOpen(false)}>Cancel</Button>
            <Button
              className="btn btn_warning"
              onClick={sendWarn}
              disabled={!warnMessage.trim()}
            >
              Send Warning
            </Button>
          </DialogActions>
        </Dialog>

        {/* Report Details Dialog */}
        <Dialog
          open={reportDetailsOpen}
          onClose={() => setReportDetailsOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Report Details</DialogTitle>
          <DialogContent>
            {selectedReportedProduct && (
              <div>
                <h4>Product: {selectedReportedProduct.title}</h4>
                <p>
                  <strong>Posted by:</strong>{" "}
                  {selectedReportedProduct.postedBy.name}
                </p>

                <h5>Reports:</h5>
                {selectedReportedProduct.reports?.length ? (
                  <TableContainer className="table_container">
                    <Table size="small">
                      <TableHead>
                        <TableRow hover>
                          <TableCell>Reason</TableCell>
                          <TableCell>Reported By</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Evidence</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedReportedProduct.reports.map(
                          (report, index) => (
                            <TableRow hover key={index}>
                              <TableCell>{report.reason}</TableCell>
                              <TableCell>{report.reportedBy}</TableCell>
                              <TableCell>{report.date}</TableCell>
                              <TableCell>{report.evidence || "-"}</TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <p>No detailed reports available.</p>
                )}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReportDetailsOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};

export default ManageProductListing;
